import React, { useEffect, useMemo, useState } from "react";
import api from "../../api/axios";
import StickyTableContainer from "../ui/StickyTableContainer";

const directFields = [
  ["machinistes", "Machinistes"],
  ["machinistes_stagiaires", "Machinistes stagiaires"],
  ["repassage_preparation", "Repassage préparation"],
  ["trassage", "Trassage"],
  ["transport", "Transport"],
  ["machines_speciales", "Machines spéciales"],
  ["trassage_special", "Trassage spécial"],
  ["controle_table", "Contrôle table"],
  ["controle_final", "Contrôle final"],
  ["machinistes_retouche", "Machinistes retouche"],
  ["repassage_final", "Repassage final"],
  ["finition", "Finition"],
  ["autre", "Autres"],
];

const indirectFields = [
  ["mag_four", "Magasin fournitures"],
  ["mag_fin", "Dépôt final"],
  ["machines_sp_manuelle", "Machines spéciales"],
  ["trassage_special", "Trassage spécial"],
  ["cont_fin", "Contrôle final"],
  ["mach_retouche", "Machin retouche"],
  ["repassage", "Repassage final"],
  ["gabaret", "Gabarit"],
  ["preparation_stagieres", "Préparation stagiaires"],
  ["preparation", "Préparation"],
  ["matlasseurs", "Matlasseurs"],
  ["coupeurs", "Coupeurs"],
  ["tiquitage", "Tiquitage"],
  ["vesline", "Vesline"],
  ["autres", "Autres"],
];

const fixedFields = [
  ["production", "Production"],
  ["qualite", "Qualité"],
  ["logistique", "Logistique"],
  ["transit", "Transit"],
  ["rh", "RH"],
  ["finance_comptabilite", "Finance-Comptabilité"],
  ["cao", "CAO"],
  ["chauffeur", "Chauffeur"],
  ["electricien", "Electricien"],
  ["forgeron", "Forgeron"],
  ["gardiennage", "Gardiennage"],
  ["mecaniciens", "Mecaniciens"],
  ["menage", "Menage"],
  ["methode", "Methode"],
  ["autres", "Autres"],
];

const today = new Date().toISOString().split("T")[0];
const cellClass = "px-5 py-3 border border-gray-300 text-center text-[#4E4A4A]";
const labelClass = "px-5 py-3 border border-gray-300 text-left font-semibold text-[#4E4A4A] whitespace-nowrap";
const stickyLabelClass = `${labelClass} sticky left-0 z-10 bg-slate-200`;
const stickyHeaderClass = `${labelClass} sticky left-0 z-20 bg-gray-100`;

const DetailedRealEffective = () => {
  const [date, setDate] = useState(today);
  const [data, setData] = useState({ chains: [], direct: {}, indirect: {}, fixed: {} });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!date) return;
    let active = true;
    setLoading(true);
    setError("");
    api.get("/effectif-reel-detail", { params: { date } })
      .then((response) => active && setData(response.data))
      .catch(() => active && setError("Impossible de charger le détail de l’effectif réel."))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [date]);

  const chainTotals = useMemo(() => Object.fromEntries(
    data.chains.map((chain) => [chain.id, directFields.reduce(
      (sum, [field]) => sum + (Number(data.direct?.[chain.id]?.[field]) || 0), 0
    )])
  ), [data]);
  const directGrandTotal = Object.values(chainTotals).reduce((sum, value) => sum + value, 0);
  const indirectTotal = indirectFields.reduce((sum, [field]) => sum + (Number(data.indirect?.[field]) || 0), 0);
  const fixedTotal = fixedFields.reduce((sum, [field]) => sum + (Number(data.fixed?.[field]) || 0), 0);
  const globalTotal = directGrandTotal + indirectTotal + fixedTotal;
  const directLaborRate = globalTotal > 0
    ? (directGrandTotal / globalTotal) * 100
    : null;

  const renderGlobalTable = (title, fields, values, totalLabel, total) => (
    <table className="dashboard-sticky-table w-full min-w-[600px] border-collapse bg-slate-200">
      <thead className="bg-gray-100">
        <tr>
          <th className={`${stickyHeaderClass} uppercase`}>{title}</th>
          <th className={`${cellClass} uppercase`}>Effectif global</th>
        </tr>
      </thead>
      <tbody>
        {fields.map(([field, label]) => (
          <tr key={field}>
            <td className={stickyLabelClass}>{label}</td>
            <td className={cellClass}>{values?.[field] || 0}</td>
          </tr>
        ))}
      </tbody>
      <tfoot className="bg-blue-50">
        <tr>
          <td className={stickyLabelClass}>{totalLabel}</td>
          <td className={`${cellClass} font-bold`}>{total}</td>
        </tr>
      </tfoot>
    </table>
  );

  const renderMobileGlobalTable = (title, fields, values, totalLabel, total) => (
    <table className="mobile-production-table w-full table-fixed border-collapse bg-white text-[9px] leading-3 text-slate-800">
      <colgroup>
        <col className="w-[72%]" />
        <col className="w-[28%]" />
      </colgroup>
      <thead className="bg-gray-100">
        <tr>
          <th className="font-semibold uppercase">{title}</th>
          <th className="font-semibold uppercase">Total</th>
        </tr>
      </thead>
      <tbody>
        {fields.map(([field, label]) => (
          <tr key={field}>
            <td className="text-left font-medium">{label}</td>
            <td className="font-semibold">{values?.[field] || 0}</td>
          </tr>
        ))}
      </tbody>
      <tfoot className="bg-blue-50 font-bold">
        <tr>
          <td className="text-left">{totalLabel}</td>
          <td>{total}</td>
        </tr>
      </tfoot>
    </table>
  );

  return (
    <section className="detailed-effective mt-8 min-w-0 border-t border-gray-200 pt-6 sm:mt-10 sm:pt-8">
      <div className="mb-5 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="text-xl font-semibold leading-tight sm:text-2xl">Suivi détaillé de l’effectif réel</h2>
          <p className="mt-1 text-sm text-gray-600 sm:text-base">Détail de l’effectif réel par chaîne</p>
        </div>
        <div>
          <label htmlFor="detailed-effective-date" className="block text-sm font-semibold mb-2">Date</label>
          <input
            id="detailed-effective-date"
            type="date"
            value={date}
            onChange={(event) => setDate(event.target.value)}
            className="block outline-0 p-2 border border-[#b3b3b3] focus:border-2 focus:border-[#2684ff] rounded"
          />
        </div>
      </div>


      {error && <p className="text-red-600 py-4">{error}</p>}
      {loading ? <p className="py-8 text-center">Chargement...</p> : !error && (
        <div className="space-y-0">
          <div className="w-full max-w-full overflow-hidden rounded border border-slate-300 md:hidden">
            <table className="mobile-production-table w-full table-fixed border-collapse bg-white text-[7px] leading-[9px] text-slate-800">
              <colgroup>
                <col className="w-[34%]" />
                {data.chains.map((chain) => <col key={chain.id} style={{ width: `${52 / Math.max(data.chains.length, 1)}%` }} />)}
                <col className="w-[14%]" />
              </colgroup>
              <thead className="bg-gray-100">
                <tr>
                  <th className="font-semibold uppercase">Effectif direct</th>
                  {data.chains.map((chain) => <th key={chain.id} className="font-semibold uppercase">{chain.name}</th>)}
                  <th className="font-semibold uppercase">Total</th>
                </tr>
              </thead>
              <tbody>
                {directFields.map(([field, label]) => {
                  const rowTotal = data.chains.reduce((sum, chain) => sum + (Number(data.direct?.[chain.id]?.[field]) || 0), 0);
                  return (
                    <tr key={field}>
                      <td className="text-left font-medium">{label}</td>
                      {data.chains.map((chain) => <td key={chain.id}>{data.direct?.[chain.id]?.[field] || 0}</td>)}
                      <td className="font-semibold">{rowTotal}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-blue-50 font-bold">
                <tr>
                  <td className="text-left">Total direct</td>
                  {data.chains.map((chain) => <td key={chain.id}>{chainTotals[chain.id] || 0}</td>)}
                  <td>{directGrandTotal}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          <div className="hidden md:block">
          <StickyTableContainer>
            <table className="dashboard-sticky-table w-full min-w-[1000px] border-collapse bg-slate-200">
              <thead className="bg-gray-100">
                <tr>
                  <th className={`${stickyHeaderClass} uppercase`}>Effectif direct horaire</th>
                  {data.chains.map((chain) => <th key={chain.id} className={`${cellClass} uppercase whitespace-nowrap`}>{chain.name}</th>)}
                  <th className={`${cellClass} uppercase whitespace-nowrap`}>Total atelier</th>
                </tr>
              </thead>
              <tbody>
                {directFields.map(([field, label]) => {
                  const rowTotal = data.chains.reduce((sum, chain) => sum + (Number(data.direct?.[chain.id]?.[field]) || 0), 0);
                  return (
                    <tr key={field}>
                      <td className={stickyLabelClass}>{label}</td>
                      {data.chains.map((chain) => <td key={chain.id} className={cellClass}>{data.direct?.[chain.id]?.[field] || 0}</td>)}
                      <td className={`${cellClass} font-semibold`}>{rowTotal}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-blue-50">
                <tr>
                  <td className={stickyLabelClass}>Total effectif réel direct horaire</td>
                  {data.chains.map((chain) => <td key={chain.id} className={`${cellClass} font-bold`}>{chainTotals[chain.id] || 0}</td>)}
                  <td className={`${cellClass} font-bold`}>{directGrandTotal}</td>
                </tr>
              </tfoot>
            </table>
          </StickyTableContainer>
          </div>

          <div className="overflow-hidden border-x border-slate-300 md:hidden">
            {renderMobileGlobalTable("Effectif indirect", indirectFields, data.indirect, "Total effectif réel indirect", indirectTotal)}
          </div>
          <div className="hidden md:block">
          <StickyTableContainer>
            {renderGlobalTable("Effectif indirect horaire", indirectFields, data.indirect, "Total effectif réel indirect horaire", indirectTotal)}
          </StickyTableContainer>
          </div>

          <div className="overflow-hidden rounded-b border border-slate-300 md:hidden">
            {renderMobileGlobalTable("Effectif réel fixe", fixedFields, data.fixed, "Total effectif réel fixe", fixedTotal)}
          </div>
          <div className="hidden md:block">
          <StickyTableContainer>
            {renderGlobalTable("Effectif réel fixe", fixedFields, data.fixed, "Total effectif réel fixe", fixedTotal)}
          </StickyTableContainer>
          </div>

          <div className="mt-6 flex items-center justify-between rounded-lg border-2 border-blue-300 bg-blue-50 px-3 py-3 sm:px-6 sm:py-5">
            <span className="text-sm font-bold uppercase text-gray-800 sm:text-lg">Total effectif réel</span>
            <span className="text-xl font-bold text-blue-700 sm:text-3xl">{globalTotal}</span>
          </div>

      <div className="my-6 rounded-lg border border-gray-200 bg-white p-4 text-sm leading-6 shadow-sm">
        <p className="font-bold">🟢 70–75 % : objectif / bonne structure</p>
        <p className="font-bold">🟠 65–70 % : acceptable, à analyser</p>
        <p className="font-bold">🔴 &lt; 65 % : poids des fonctions indirectes élevé</p>
        <p className="font-bold">⚠️ &gt; 80 % : vérifier que les fonctions support ne sont pas sous-dimensionnées</p>
      </div>

          <div className="mt-4 flex items-center justify-between rounded-lg border-2 border-emerald-300 bg-emerald-50 px-3 py-3 sm:px-6 sm:py-5">
            <span className="text-sm font-bold text-gray-800 sm:text-lg">Taux main d'œuvre direct =</span>
            <span className="text-xl font-bold text-emerald-700 sm:text-3xl">
              {directLaborRate === null ? "—" : `${Math.round(directLaborRate)} %`}
            </span>
          </div>
        </div>
      )}
    </section>
  );
};

export default DetailedRealEffective;
